import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

/**
 * A reusable card component for displaying structured information with optional interactivity.
 *
 * The OrgbrainCardComponent provides a customizable layout including a header, label,
 * subheader, button, and optional toolbar buttons. It supports interaction through
 * click events on the card and the toolbar.
 *
 * @example
 * ```html
 * <orgbrain-card
 *   [header]="'Project Alpha'"
 *   [label]="'Due Soon'"
 *   [labelAsTag]="true"
 *   [labelBg]="'bg-[#FF5722]'"
 *   [subheader]="'Deadline: July 20'"
 *   [button]="'Details'"
 *   [type]="'medium'"
 *   [showButtons]="true"
 *   [extraButtons]="[
 *     { icon: 'edit', id: 'editBtn', tooltip: 'Edit' },
 *     { icon: 'delete', id: 'deleteBtn', tooltip: 'Delete' }
 *   ]"
 *   (cardClick)="onCardClick()"
 *   (toolbarClick)="onToolbarClick()"
 * ></orgbrain-card>
 * ```
 *
 * @param header The main heading of the card. Styled as 18px font.
 * @param label A small label shown opposite the header, typically in gray.
 * @param labelAsTag Whether the label should be displayed as a tag.
 * @param labelBg Background color class for the label tag (e.g., Tailwind CSS class).
 * @param subheader Text displayed below the header for additional context.
 * @param button Label for a button displayed on the card.
 * @param type Determines the size of the card. Can be 'small' or 'medium'.
 * @param showButtons Whether extra toolbar buttons should be shown (always or on hover).
 * @param extraButtons An array of icon buttons to be displayed on the card's toolbar.
 * Each button has:
 * - `icon`: Icon name (string),
 * - `id`: Identifier for the button (string),
 * - `tooltip`: Tooltip text for the button (string).
 * @param cardClick Emits when the main area of the card is clicked.
 * @param toolbarClick Emits when the toolbar is clicked.
 */

@Component({
  selector: 'orgbrain-card',
  templateUrl: './orgbrain-card.component.html',
  styleUrls: ['./orgbrain-card.component.scss'],
  standalone: false,
})
export class OrgbrainCardComponent implements OnInit {
  @Output() cardClick = new EventEmitter<void>();
  @Output() toolbarClick = new EventEmitter<string>();
  @Input() header: string = 'Header';
  @Input() label: string = 'Small Label';
  @Input() labelAsTag: boolean = false;
  @Input() labelBg: string = 'bg-[#0097A7]';
  @Input() subheader: string = 'Subheader text';
  @Input() button: string = 'Button';
  @Input() type: 'small' | 'medium' = 'small';
  @Input() showButtons: boolean = false;
  @Input() extraButtons: {
    icon: string;
    id: string;
    tooltip: string;
  }[];

  constructor() {}

  ngOnInit() {}
}
